import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { DashboardApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { notifyError, notifySuccess } from '../utils/statusShortcuts';
import './AccessPage.css';

const groupByCategory = (definitions = []) => definitions.reduce((acc, definition) => {
  const category = definition.category || 'Tilganger';
  if (!acc[category]) acc[category] = [];
  acc[category].push(definition);
  return acc;
}, {});

const EFFECT_OPTIONS = [
  { value: 'deny', label: 'Deny', icon: '×' },
  { value: 'inherit', label: 'Inherit', icon: '−' },
  { value: 'allow', label: 'Allow', icon: '✓' },
];

const buildPermissionEffectMap = (group) => {
  const map = {};
  if (Array.isArray(group?.permissionRules)) {
    group.permissionRules.forEach((rule) => {
      if ((rule.effect === 'allow' || rule.effect === 'deny') && rule.permissionKey) {
        map[rule.permissionKey] = rule.effect;
      }
    });
    return map;
  }
  (Array.isArray(group?.permissionKeys) ? group.permissionKeys : []).forEach((permissionKey) => {
    map[permissionKey] = 'allow';
  });
  return map;
};

const buildProductEffectMap = (group, products = []) => {
  const map = {};
  if (Array.isArray(group?.productRules)) {
    group.productRules.forEach((rule) => {
      if ((rule.effect === 'allow' || rule.effect === 'deny') && rule.productId) {
        map[String(rule.productId)] = rule.effect;
      }
    });
    return map;
  }
  if (group?.productScope?.mode === 'all') {
    products.forEach((product) => {
      map[String(product.id)] = 'allow';
    });
    return map;
  }
  (Array.isArray(group?.productScope?.allowedProductIds) ? group.productScope.allowedProductIds : []).forEach((productId) => {
    map[String(productId)] = 'allow';
  });
  return map;
};

const effectMapToPermissionRules = (effectMap = {}) => Object.entries(effectMap)
  .filter(([, effect]) => effect === 'allow' || effect === 'deny')
  .map(([permissionKey, effect]) => ({ permissionKey, effect }));

const effectMapToProductRules = (effectMap = {}) => Object.entries(effectMap)
  .filter(([, effect]) => effect === 'allow' || effect === 'deny')
  .map(([productId, effect]) => ({ productId: Number(productId), effect }))
  .filter((rule) => Number.isInteger(rule.productId) && rule.productId > 0);

const createEmptyDraft = () => ({
  name: '',
  description: '',
  permissionEffects: {},
  productEffects: {},
  unmatchedInboxEffect: 'inherit',
});

const createDraftFromGroup = (group, products = []) => {
  if (!group) return createEmptyDraft();
  return {
    name: group.name || '',
    description: group.description || '',
    permissionEffects: buildPermissionEffectMap(group),
    productEffects: buildProductEffectMap(group, products),
    unmatchedInboxEffect: group.unmatchedInboxEffect || (group.productScope?.allowUnmatchedInbox ? 'allow' : 'inherit'),
  };
};

const effectSignature = (effectMap = {}) => JSON.stringify(
  Object.entries(effectMap)
    .filter(([, effect]) => effect === 'allow' || effect === 'deny')
    .sort(([a], [b]) => String(a).localeCompare(String(b))),
);

const EffectToggle = ({ value = 'inherit', onChange, disabled = false, label }) => (
  <div className="access-effect-toggle" role="group" aria-label={label}>
    {EFFECT_OPTIONS.map((option) => (
      <button
        key={option.value}
        type="button"
        className={`access-effect-option access-effect-option--${option.value}${value === option.value ? ' is-active' : ''}`}
        onClick={() => onChange(option.value)}
        disabled={disabled}
      >
        <span aria-hidden="true">{option.icon}</span>
        {option.label}
      </button>
    ))}
  </div>
);

const AccessPage = () => {
  const { selectedCompanyId, selectedCompany, permissions } = useAuth();
  const companyId = selectedCompanyId || selectedCompany?.id || null;
  const canManageAccess = Boolean(permissions?.canManageAccess || permissions?.isOwner);
  const canViewAccess = Boolean(canManageAccess || permissions?.canViewAccess);

  const [payload, setPayload] = useState(null);
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [draft, setDraft] = useState(() => createEmptyDraft());

  const groups = useMemo(() => (Array.isArray(payload?.groups) ? payload.groups : []), [payload?.groups]);
  const products = useMemo(() => (Array.isArray(payload?.products) ? payload.products : []), [payload?.products]);
  const permissionDefinitions = useMemo(
    () => (Array.isArray(payload?.permissionDefinitions) ? payload.permissionDefinitions : []),
    [payload?.permissionDefinitions],
  );
  const selectedGroup = useMemo(
    () => groups.find((group) => Number(group.id) === Number(selectedGroupId)) || null,
    [groups, selectedGroupId],
  );

  const loadAccess = useCallback(async () => {
    if (!companyId || !canViewAccess) {
      setPayload(null);
      return;
    }
    setLoading(true);
    try {
      const response = await DashboardApi.getCompanyAccess(companyId);
      setPayload(response || null);
      const nextGroups = Array.isArray(response?.groups) ? response.groups : [];
      setSelectedGroupId((current) => (
        nextGroups.some((group) => Number(group.id) === Number(current))
          ? current
          : null
      ));
    } catch (error) {
      notifyError(error.message || 'Kunne ikke hente tilganger.', { title: 'Tilganger' });
    } finally {
      setLoading(false);
    }
  }, [canViewAccess, companyId]);

  useEffect(() => {
    loadAccess();
  }, [loadAccess]);

  useEffect(() => {
    setDraft(createDraftFromGroup(selectedGroup, products));
  }, [products, selectedGroup]);

  const permissionGroups = useMemo(() => groupByCategory(permissionDefinitions), [permissionDefinitions]);
  const baselineDraft = useMemo(() => createDraftFromGroup(selectedGroup, products), [products, selectedGroup]);
  const dirtyFlags = useMemo(() => {
    if (!selectedGroup) {
      return {
        group: false,
        permissions: false,
        products: false,
      };
    }
    return {
      group: draft.name.trim() !== baselineDraft.name.trim()
        || draft.description.trim() !== baselineDraft.description.trim(),
      permissions: effectSignature(draft.permissionEffects) !== effectSignature(baselineDraft.permissionEffects),
      products: effectSignature(draft.productEffects) !== effectSignature(baselineDraft.productEffects)
        || (draft.unmatchedInboxEffect || 'inherit') !== (baselineDraft.unmatchedInboxEffect || 'inherit'),
    };
  }, [baselineDraft, draft, selectedGroup]);
  const hasUnsavedChanges = Boolean(
    canManageAccess
    && selectedGroup
    && (dirtyFlags.group || dirtyFlags.permissions || dirtyFlags.products),
  );

  const applyUpdatedPayload = (response, { keepSelection = true } = {}) => {
    setPayload(response || null);
    const updatedGroups = Array.isArray(response?.groups) ? response.groups : [];
    setSelectedGroupId((current) => (
      keepSelection && updatedGroups.some((group) => Number(group.id) === Number(current))
        ? current
        : null
    ));
  };

  const handleCreateGroup = async (event) => {
    event.preventDefault();
    const name = newGroupName.trim();
    if (!companyId || !name || !canManageAccess) return;
    setSaving(true);
    try {
      const response = await DashboardApi.createPermissionGroup(companyId, { name });
      setNewGroupName('');
      applyUpdatedPayload(response, { keepSelection: false });
      notifySuccess('Tilgangsgruppen ble opprettet.', { source: 'Tilganger' });
    } catch (error) {
      notifyError(error.message || 'Kunne ikke opprette tilgangsgruppe.', { title: 'Tilganger' });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteGroup = async () => {
    if (!companyId || !selectedGroup || !canManageAccess) return;
    if (!window.confirm(`Slette tilgangsgruppen "${selectedGroup.name}"?`)) return;
    setSaving(true);
    try {
      const response = await DashboardApi.deletePermissionGroup(companyId, selectedGroup.id);
      applyUpdatedPayload(response, { keepSelection: false });
      notifySuccess('Tilgangsgruppen ble slettet.', { source: 'Tilganger' });
    } catch (error) {
      notifyError(error.message || 'Kunne ikke slette tilgangsgruppe.', { title: 'Tilganger' });
    } finally {
      setSaving(false);
    }
  };

  const saveChanges = async () => {
    if (!companyId || !selectedGroup || !canManageAccess || !hasUnsavedChanges) return;
    if (dirtyFlags.group && !draft.name.trim()) {
      notifyError('Navn er påkrevd for tilgangsgruppen.', { title: 'Tilganger' });
      return;
    }
    setSaving(true);
    try {
      let response = null;
      if (dirtyFlags.group) {
        response = await DashboardApi.updatePermissionGroup(companyId, selectedGroup.id, {
          name: draft.name,
          description: draft.description,
        });
      }
      if (dirtyFlags.permissions) {
        response = await DashboardApi.updatePermissionGroupPermissions(
          companyId,
          selectedGroup.id,
          effectMapToPermissionRules(draft.permissionEffects),
        );
      }
      if (dirtyFlags.products) {
        response = await DashboardApi.updatePermissionGroupProducts(companyId, selectedGroup.id, {
          productRules: effectMapToProductRules(draft.productEffects),
          unmatchedInboxEffect: draft.unmatchedInboxEffect,
        });
      }
      if (response) applyUpdatedPayload(response);
      notifySuccess('Endringene ble lagret.', { source: 'Tilganger' });
    } catch (error) {
      notifyError(error.message || 'Kunne ikke lagre endringer.', { title: 'Tilganger' });
    } finally {
      setSaving(false);
    }
  };

  const discardChanges = () => {
    setDraft(createDraftFromGroup(selectedGroup, products));
  };

  const setPermissionEffect = (permissionKey, effect) => {
    setDraft((current) => {
      const next = { ...(current.permissionEffects || {}) };
      if (effect === 'inherit') delete next[permissionKey];
      else next[permissionKey] = effect;
      return { ...current, permissionEffects: next };
    });
  };

  const setProductEffect = (productId, effect) => {
    setDraft((current) => {
      const next = { ...(current.productEffects || {}) };
      const key = String(productId);
      if (effect === 'inherit') delete next[key];
      else next[key] = effect;
      return {
        ...current,
        productEffects: next,
      };
    });
  };

  if (!companyId) {
    return <div className="page"><div className="card"><h1>Tilganger</h1><p>Velg et selskap først.</p></div></div>;
  }

  if (!canViewAccess) {
    return <div className="page"><div className="card"><h1>Tilganger</h1><p>Du mangler tilgang til tilgangsgrupper.</p></div></div>;
  }

  return (
    <div className="page access-page">
      <div className="page-header">
        <div>
          <p className="page-eyebrow">Selskap</p>
          <h1>Tilganger</h1>
          <p className="page-subtitle">Administrer tilgangsgrupper for {selectedCompany?.name || 'selskapet'}.</p>
          {!canManageAccess && <p className="page-subtitle">Du har kun lesetilgang.</p>}
        </div>
      </div>

      <div className="access-layout">
        <aside className="card access-groups-panel">
          <h2>Grupper</h2>
          <form className="access-create-form" onSubmit={handleCreateGroup}>
            <input
              className="input"
              value={newGroupName}
              onChange={(event) => setNewGroupName(event.target.value)}
              placeholder="Ny gruppe"
              disabled={saving || !canManageAccess}
            />
            <button type="submit" className="button button-primary" disabled={saving || !canManageAccess || !newGroupName.trim()}>
              Opprett
            </button>
          </form>
          {loading ? (
            <p className="text-muted">Laster tilganger...</p>
          ) : groups.length === 0 ? (
            <p className="text-muted">Ingen grupper er opprettet.</p>
          ) : (
            <div className="access-group-list">
              {groups.map((group) => (
                <button
                  key={group.id}
                  type="button"
                  className={`access-group-item${Number(group.id) === Number(selectedGroup?.id) ? ' is-active' : ''}`}
                  onClick={() => setSelectedGroupId(group.id)}
                >
                  <span>{group.name}</span>
                  <small>{group.memberIds?.length || 0} medlemmer</small>
                </button>
              ))}
            </div>
          )}
        </aside>

        <section className="access-editor">
          {!selectedGroup ? (
            <div className="card access-empty">
              <h2>Opprett en gruppe</h2>
              <p>Grupper gir ansatte tilgang til sider, handlinger og produkter.</p>
            </div>
          ) : (
            <>
              <div className="card access-section">
                <div className="card-header">
                  <h2>Gruppe</h2>
                  <button type="button" className="button button-danger-outline button-small" onClick={handleDeleteGroup} disabled={saving || !canManageAccess}>
                    Slett
                  </button>
                </div>
                <div className="access-form-grid">
                  <label>
                    Navn
                    <input className="input" value={draft.name} disabled={!canManageAccess} onChange={(event) => setDraft((current) => ({ ...current, name: event.target.value }))} />
                  </label>
                  <label>
                    Beskrivelse
                    <input className="input" value={draft.description} disabled={!canManageAccess} onChange={(event) => setDraft((current) => ({ ...current, description: event.target.value }))} />
                  </label>
                </div>
              </div>

              <div className="card access-section">
                <div className="card-header">
                  <h2>Sider og handlinger</h2>
                </div>
                {Object.entries(permissionGroups).map(([category, definitions]) => (
                  <div key={category} className="access-permission-category">
                    <h3>{category}</h3>
                    <div className="access-rule-list">
                      {definitions.map((definition) => (
                        <div key={definition.key} className="access-rule-row">
                          <div className="access-rule-copy">
                            <strong>{definition.label}</strong>
                            <small>{definition.description}</small>
                          </div>
                          <EffectToggle
                            value={draft.permissionEffects?.[definition.key] || 'inherit'}
                            disabled={!canManageAccess}
                            onChange={(effect) => setPermissionEffect(definition.key, effect)}
                            label={`Tilgang for ${definition.label}`}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <div className="card access-section">
                <div className="card-header">
                  <h2>Produkttilgang</h2>
                </div>
                <div className="access-rule-list">
                  <div className="access-rule-row">
                    <div className="access-rule-copy">
                      <strong>Henvendelser uten produkt</strong>
                      <small>Gjelder innbokselementer som ikke er koblet til et produkt.</small>
                    </div>
                    <EffectToggle
                      value={draft.unmatchedInboxEffect || 'inherit'}
                      disabled={!canManageAccess}
                      onChange={(effect) => setDraft((current) => ({ ...current, unmatchedInboxEffect: effect }))}
                      label="Tilgang til henvendelser uten produkt"
                    />
                  </div>
                  {products.map((product) => (
                    <div key={product.id} className="access-rule-row">
                      <div className="access-rule-copy">
                        <strong>
                          {product.colorCode ? (
                            <span
                              className="access-product-swatch"
                              style={{ backgroundColor: product.colorCode }}
                              aria-hidden="true"
                            />
                          ) : null}
                          {product.name}
                        </strong>
                        <small>{product.botName}</small>
                      </div>
                      <EffectToggle
                        value={draft.productEffects?.[String(product.id)] || 'inherit'}
                        disabled={!canManageAccess}
                        onChange={(effect) => setProductEffect(product.id, effect)}
                        label={`Produkttilgang for ${product.name}`}
                      />
                    </div>
                  ))}
                  {products.length === 0 && (
                    <p className="text-muted">Ingen aktive produkter er tilgjengelige.</p>
                  )}
                </div>
              </div>
            </>
          )}
        </section>
      </div>
      {hasUnsavedChanges && (
        <div className="access-save-prompt" role="status" aria-live="polite">
          <div>
            <strong>Ulagrede endringer</strong>
            <p>Endringene forkastes hvis du forlater siden.</p>
          </div>
          <div className="access-save-prompt-actions">
            <button
              type="button"
              className="button button-secondary button-small"
              onClick={discardChanges}
              disabled={saving}
            >
              Forkast
            </button>
            <button
              type="button"
              className="button button-primary button-small"
              onClick={saveChanges}
              disabled={saving || (dirtyFlags.group && !draft.name.trim())}
            >
              {saving ? 'Lagrer...' : 'Lagre endringer'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccessPage;
